// Tile-search helpers shared by structures, builders, and the mobile HQ:
// IsTileFree (game/misc.cpp:112-158) and FindNearestFreeTile over the 224-entry outward
// spiral (game/Builder.cpp:437-473) — delivery positions and attack-point fallbacks come
// from this exact candidate order.

import type { SimContext } from '../context.ts';
import type { Gob } from './gob.ts';
import { AnyTransitionsIntoTile } from './mobileunit.ts';
import { kbfStructure, kbfMobileUnit } from '../terrain/terrainmap.ts';
import { kgidNull } from '../sm/message.ts';
import { WcFromTc } from '../math/coords.ts';
import { kwcTileHalf } from './unitconsts.ts';
import type { WPoint } from '../types.ts';

// game/misc.cpp:112-158
export function IsTileFree(
  ctx: SimContext,
  tx: number,
  ty: number,
  bf: number = kbfStructure,
): { free: boolean; pgob: Gob | null } {
  const { ctx: ctxMap, cty } = ctx.gobm.GetMapSize();
  if (tx < 0 || tx >= ctxMap || ty < 0 || ty >= cty) return { free: false, pgob: null };

  // Needs to be free of gobs
  if (bf & (kbfMobileUnit | kbfStructure)) {
    for (let gid = ctx.gobm.GetFirstGid(tx, ty); gid !== kgidNull; gid = ctx.gobm.GetNextGid(gid)) {
      const pgob = ctx.gobm.GetGob(gid);
      if (pgob !== null) return { free: false, pgob };
    }
  }

  // No gobs can be transitioning into this tile
  if (bf & kbfMobileUnit) {
    const pgob = AnyTransitionsIntoTile(ctx, tx, ty, null);
    if (pgob !== null) return { free: false, pgob };
  }

  if (ctx.trmap!.IsBlocked(tx, ty, bf)) return { free: false, pgob: null };

  return { free: true, pgob: null };
}

// game/Builder.cpp:437-454 — the outward spiral of candidate tiles (SIM DATA: unit
// delivery + attack-point fallbacks position through it, in this exact order)
const s_aptInit: number[] = [
  0, 0, 0, -1, -1, 0, 1, 0, 0, 1, -1, -1, 1, -1, -1, 1, 1, 1, 0, -2, -2, 0, 2, 0, 0, 2, -1, -2, 1,
  -2, -2, -1, 2, -1, -2, 1, 2, 1, -1, 2, 1, 2, -2, -2, 2, -2, -2, 2, 2, 2, 0, -3, -3, 0, 3, 0, 0, 3,
  -1, -3, 1, -3, -3, -1, 3, -1, -3, 1, 3, 1, -1, 3, 1, 3, -2, -3, 2, -3, -3, -2, 3, -2, -3, 2, 3, 2,
  -2, 3, 2, 3, 0, -4, -4, 0, 4, 0, 0, 4, -1, -4, 1, -4, -4, -1, 4, -1, -4, 1, 4, 1, -1, 4, 1, 4, -3,
  -3, 3, -3, -3, 3, 3, 3, -2, -4, 2, -4, -4, -2, 4, -2, -4, 2, 4, 2, -2, 4, 2, 4, 0, -5, -3, -4, 3,
  -4, -4, -3, 4, -3, -5, 0, 5, 0, -4, 3, 4, 3, -3, 4, 3, 4, 0, 5, -1, -5, 1, -5, -5, -1, 5, -1, -5,
  1, 5, 1, -1, 5, 1, 5, -2, -5, 2, -5, -5, -2, 5, -2, -5, 2, 5, 2, -2, 5, 2, 5, -4, -4, 4, -4, -4,
  4, 4, 4, -3, -5, 3, -5, -5, -3, 5, -3, -5, 3, 5, 3, -3, 5, 3, 5, 0, -6, -6, 0, 6, 0, 0, 6, -1, -6,
  1, -6, -6, -1, 6, -1, -6, 1, 6, 1, -1, 6, 1, 6, -2, -6, 2, -6, -6, -2, 6, -2, -6, 2, 6, 2, -2, 6,
];

// game/Builder.cpp:456-473 — NOTE the default mask (ht.h:8759): structure|mobile-unit,
// NOT IsTileFree's own kbfStructure default — unit delivery avoids tiles units are
// standing on or transitioning into
export function FindNearestFreeTile(
  ctx: SimContext,
  tx: number,
  ty: number,
  bf: number = kbfStructure | kbfMobileUnit,
): WPoint {
  const cpt = s_aptInit.length / 2;
  for (let npt = 0; npt < cpt; npt++) {
    const txT = tx + s_aptInit[npt * 2];
    const tyT = ty + s_aptInit[npt * 2 + 1];
    if (IsTileFree(ctx, txT, tyT, bf).free) {
      return { wx: WcFromTc(txT) + kwcTileHalf, wy: WcFromTc(tyT) + kwcTileHalf };
    }
  }
  return { wx: WcFromTc(tx) + kwcTileHalf, wy: WcFromTc(ty) + kwcTileHalf };
}
